import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Box, Heading, Select, Spinner, HStack } from "@chakra-ui/react";
import { ClientAnalyticsAmount } from "../../../api/clientUser/getClientAnalytics";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface AmountChartProps {
  data: ClientAnalyticsAmount[];
  timeFrame: "day" | "month" | "year";
  onTimeFrameChange: (timeFrame: "day" | "month" | "year") => void;
  isLoading?: boolean;
}

export const AmountChart = ({ data, timeFrame, onTimeFrameChange, isLoading = false }: AmountChartProps) => {

  const { labels, chartData } = useMemo(() => {
    // API already returns aggregated data by timeFrame
    // Just format the labels for display
    return {
      labels: data.map((d) => {
        const date = new Date(d.date);
        // Format label based on timeFrame
        switch (timeFrame) {
          case "day":
            return date.toLocaleDateString();
          case "month":
            return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          case "year":
            return date.getFullYear().toString();
          default:
            return d.date;
        }
      }),
      chartData: data.map((d) => d.amount / 100),
    };
  }, [data, timeFrame]);

  const dataConfig = {
    labels,
    datasets: [
      {
        label: "Amount Raised ($)",
        data: chartData,
        fill: true,
        backgroundColor: "rgba(51, 156, 255, 0.1)",
        borderColor: "#339CFF",
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: "#339CFF",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointHoverRadius: 6,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          font: {
            size: 14,
            weight: "bold",
          },
          color: "#1A202C",
          padding: 15,
        },
      },
      tooltip: {
        backgroundColor: "rgba(26, 32, 44, 0.95)",
        padding: 12,
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 13,
        },
        borderColor: "#339CFF",
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
          color: "#718096",
        },
      },
      y: {
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          font: {
            size: 12,
          },
          color: "#718096",
          callback: function(value: any) {
            return "$" + value.toLocaleString();
          },
        },
      },
    },
  };

  return (
    <Box
      bg="white"
      p={{ base: 4, md: 8 }}
      borderRadius={{ base: "16px", md: "24px" }}
      boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
      border="1px solid"
      borderColor="gray.100"
      overflow="hidden"
      maxW="100%"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="md" color="#1A202C" fontWeight="700">
          Amount Raised Over Time
        </Heading>
        <HStack spacing={2}>
          <Select
            value={timeFrame}
            onChange={(e) => onTimeFrameChange(e.target.value as "day" | "month" | "year")}
            size="md"
            maxW="150px"
            borderRadius="8px"
            focusBorderColor="#339CFF"
            fontWeight="600"
          >
            {["day", "month", "year"].map((frame) => (
              <option key={frame} value={frame}>
                {frame.charAt(0).toUpperCase() + frame.slice(1)}
              </option>
            ))}
          </Select>
          {isLoading && <Spinner size="sm" color="#339CFF" />}
        </HStack>
      </Box>
      <Line data={dataConfig} options={options} />
    </Box>
  );
};
